"use client";
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { apiSubscribeNewsletter } from '@/apis/page_optimization';

interface Props {
    companyId: string;
}

const Newsletter = ({ companyId }: Props) => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);

    const validateEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

    const handleSubscribe = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) { toast.error('Please enter your email address.'); return; }
        if (!validateEmail(email)) { toast.error('Please enter a valid email address.'); return; }

        setLoading(true);
        try {
            const response = await apiSubscribeNewsletter(companyId, email);
            if (response.message === 'Subscribed successfully') {
                toast.success('Thank you for subscribing!', { autoClose: 2000 });
                setEmail('');
            } else {
                toast.error('You have already subscribed to our newsletter.');
            }
        } catch {
            toast.error('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className="w-full h-[3px]" style={{ background: 'linear-gradient(90deg, transparent, #7AFC20 30%, #7AFC20 70%, transparent)' }} />
            <section aria-label="Newsletter Section" className="relative w-full py-10 md:py-14 overflow-hidden"
                style={{ background: '#F5F5DC' }}>

                {/* Background glows */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] rounded-full blur-[150px] opacity-25 pointer-events-none" style={{ background: '#7AFC20' }} />

                <div className="container mx-auto px-4 md:px-8 relative z-10">
                    <div className="relative overflow-hidden p-8 md:p-12 lg:p-16 flex flex-col lg:flex-row items-center justify-between gap-10"
                        style={{ background: '#081C11', clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 20px 100%, 0 calc(100% - 20px))' }}>

                        {/* Background pattern */}
                        <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
                            style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #7AFC20 1px, transparent 0)', backgroundSize: '28px 28px' }} />
                        <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full opacity-[0.04] blur-[100px] pointer-events-none" style={{ background: '#7AFC20' }} />

                        {/* Corner accents */}
                        <div className="absolute top-0 left-0 w-16 h-[2px]" style={{ background: '#7AFC20' }} />
                        <div className="absolute top-0 left-0 w-[2px] h-16" style={{ background: '#7AFC20' }} />
                        <div className="absolute bottom-0 right-0 w-16 h-[2px]" style={{ background: '#7AFC20' }} />
                        <div className="absolute bottom-0 right-0 w-[2px] h-16" style={{ background: '#7AFC20' }} />

                        {/* Left content */}
                        <div className="flex-1 text-center lg:text-left relative z-10">
                            <div className="flex items-center gap-3 mb-4 justify-center lg:justify-start">
                                <div className="h-px w-8 bg-gradient-to-r from-[#7AFC20] to-transparent" />
                                <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#7AFC20] drop-shadow-[0_0_8px_rgba(122,252,32,0.5)]">
                                    Stay In The Game
                                </span>
                            </div>
                            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black leading-[1.1] m-0 mb-4">
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7AFC20] to-[#5cd010]">Never Miss </span>
                                <span className="text-[#F5F5DC]">A Deal Again</span>
                            </h2>
                            <p className="text-[14px] leading-relaxed font-medium max-w-md mx-auto lg:mx-0" style={{ color: 'rgba(245,245,220,0.45)' }}>
                                Join <span className="text-[#7AFC20] font-black">50,000+</span> athletes & fitness fans getting exclusive deals straight to their inbox.
                            </p>

                            {/* Stats row */}
                            <div className="flex items-center gap-6 mt-6 justify-center lg:justify-start">
                                {[['50K+', 'Subscribers'], ['Daily', 'Updates'], ['Free', 'Forever']].map(([val, label]) => (
                                    <div key={label} className="flex flex-col items-center lg:items-start">
                                        <span className="text-lg font-black" style={{ color: '#7AFC20' }}>{val}</span>
                                        <span className="text-[9px] font-black uppercase tracking-widest" style={{ color: 'rgba(245,245,220,0.35)' }}>{label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Right: Form */}
                        <div className="w-full lg:max-w-md relative z-10">
                            <form onSubmit={handleSubscribe} className="flex flex-col gap-3">
                                <div className="relative">
                                    <input
                                        type="email"
                                        placeholder="Enter your email address"
                                        className="w-full px-5 py-4 text-sm font-medium outline-none transition-all duration-300"
                                        style={{
                                            background: 'rgba(245,245,220,0.06)',
                                            border: '1px solid rgba(122,252,32,0.25)',
                                            color: '#F5F5DC',
                                            clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))',
                                        }}
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        disabled={loading}
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="group relative overflow-hidden w-full py-4 font-black text-xs uppercase tracking-[0.2em] transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-70 flex items-center justify-center gap-2"
                                    style={{ background: '#7AFC20', color: '#081C11', clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))' }}>
                                    <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 skew-x-12"
                                        style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)' }} />
                                    <span className="relative z-10">{loading ? 'Subscribing...' : 'Subscribe Now'}</span>
                                    {!loading && (
                                        <svg className="relative z-10 w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
                                        </svg>
                                    )}
                                </button>
                            </form>
                            <p className="mt-3 text-[10px] font-medium text-center" style={{ color: 'rgba(245,245,220,0.25)' }}>
                                * By subscribing, you agree to our Terms of Service and Privacy Policy.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-[3px]" style={{ background: 'linear-gradient(90deg, transparent, #081C11 30%, #081C11 70%, transparent)' }} />
            </section>
        </>
    );
};

export default Newsletter;
